import {ChangeDetectionStrategy, Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {Store} from '@ngrx/store';

import {Lens} from './../shared/model/lens';
import {MessageService} from './../../services/message.service';
import {
  CreateNewLensAction,
  DeleteLensAction,
  GetLensesAction,
  UpdateLensAction,
} from './../../core/store/lenses/lenses.action';
import {OphInputOrangeComponent} from './../../shared/design/oph-inputs/oph-input-orange/oph-input-orange.component';
import {createDuplicateValidator} from './../../shared/utils/duplicate-fc-validator';
import {OphDialogFooterComponent} from './../../shared/design/oph-dialog-footer/oph-dialog-footer.component';

export type LensDialogData = {
  lens: Lens;
  lenses?: Lens[];
};

@Component({
  selector: 'lense-dialog',
  standalone: true,
  imports: [OphInputOrangeComponent, FormsModule, ReactiveFormsModule, OphDialogFooterComponent, MatIconModule],
  templateUrl: './lense-dialog.component.html',
  styleUrl: './lense-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LenseDialogComponent implements OnInit {
  readonly data = inject<LensDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<LensDialogData>);
  private messageService = inject(MessageService);
  private store$ = inject(Store);
  private fb = inject(FormBuilder);

  private readonly duplicateLenseValidator = createDuplicateValidator((value: string) => {
    if (!this.data.lenses) return false;
    const displayName = value.toLowerCase();
    return this.data.lenses.some(
      lense => lense.title.toLowerCase() === displayName && lense._id !== this.data.lens?._id
    );
  });

  public lensFg = this.fb.group({
    _id: this.data.lens?._id || null,
    title: [this.data.lens?.title || '', [Validators.required, this.duplicateLenseValidator]],
    settings: this.data.lens?.settings || {},
  });

  public menuOptions = ['delete'];

  isLoading = false;
  lensHasId = false;

  ngOnInit(): void {
    this.lensHasId = !!this.data.lens?._id;
    this.lensFg = this.fb.group({
      _id: this.data.lens?._id || null,
      title: [this.data.lens?.title || '', [Validators.required, this.duplicateLenseValidator]],
      settings: this.data.lens?.settings || {},
    });
  }

  footerActionLabels() {
    return {
      cancel: 'Cancel',
      save: this.lensHasId ? 'Update Lens' : 'Create Lens',
    };
  }

  onSave() {
    if (this.lensHasId) {
      this.updateLens();
      return;
    }
    this.createLens();
  }

  onCancel() {
    this.dialogRef.close();
  }

  onMenuOption(type: (typeof this.menuOptions)[number]) {
    if (type === 'delete') {
      this.deleteLens();
    }
  }

  createLens(): void {
    if (this.lensFg.invalid) {
      this.lensFg.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    this.store$.dispatch(
      new CreateNewLensAction({
        title: this.lensFg.get('title')?.value,
        onSuccess: () => {
          this.isLoading = false;
          this.dialogRef.close(this.lensFg.value);
          this.messageService.add(`Lens created successfully`, 'newTaskAssigned');
          this.store$.dispatch(new GetLensesAction({}));
        },
        onFailure: error => {
          this.isLoading = false;
          this.messageService.add(`Error creating lens`, 'error');
          console.error('Error creating lens:', error);
        },
      })
    );
  }

  updateLens(): void {
    if (this.lensFg.invalid) {
      this.lensFg.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    this.store$.dispatch(
      new UpdateLensAction({
        lens: {...this.lensFg.value, settings: this.data.lens.settings} as Lens,
        onSuccess: () => {
          this.isLoading = false;
          this.dialogRef.close(this.lensFg.value);
          this.messageService.add(`Lens updated successfully`, 'newTaskAssigned');
          this.store$.dispatch(new GetLensesAction({}));
        },
        onFailure: error => {
          this.isLoading = false;
          this.messageService.add(`Error updating lens`, 'error');
          console.error('Error updating lens:', error);
        },
      })
    );
  }

  deleteLens() {
    this.isLoading = true;

    const {title} = this.data.lens;

    this.store$.dispatch(
      new DeleteLensAction({
        id: this.data.lens._id,
        onSuccess: () => {
          this.store$.dispatch(new GetLensesAction({}));
          this.messageService.add(`Lens "${title}" deleted successfully`, 'taskCompleted');
          this.isLoading = false;
          this.dialogRef.close();
        },
        onFailure: (error: Error) => {
          this.messageService.add(`Error deleting lens "${title}": ${error.message}`, 'error');
        },
      })
    );
  }
}
